import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaQuestionCircle, FaChevronDown } from 'react-icons/fa';
import './Faq.scss';

const faqsLeft = [
  {
    question: 'What services do you offer?',
    answer: 'We offer web development, mobile app development, UI/UX design, e-commerce solutions, digital marketing, cloud solutions, and DevOps.'
  },
  {
    question: 'How do I get a project quote?',
    answer: 'Simply contact us through our website or email, and we will provide a free, detailed project quote after understanding your requirements.'
  },
  {
    question: 'What is your project delivery process?',
    answer: 'Our process includes discovery, planning, design, development, testing, launch, and ongoing support. We keep you updated at every step.'
  },
  {
    question: 'Do you provide post-launch support?',
    answer: 'Yes, we offer 24/7 support, maintenance, and updates to ensure your digital assets remain secure and up-to-date.'
  },
  {
    question: 'Are your solutions mobile-friendly and SEO-optimized?',
    answer: 'Absolutely! All our solutions are fully responsive and built with SEO best practices for maximum visibility.'
  },
  {
    question: 'How do you ensure project quality?',
    answer: 'We follow strict QA processes, code reviews, and user testing to deliver flawless, high-quality results.'
  },
  {
    question: 'Can you work with startups and enterprises?',
    answer: 'Yes, we tailor our services for businesses of all sizes, from startups to large enterprises.'
  }
];

const faqsRight = [
  {
    question: 'How long does a typical project take?',
    answer: 'Project timelines vary, but most websites and apps are delivered within 4-12 weeks depending on complexity and requirements.'
  },
  {
    question: 'Will I be able to update my website/app myself?',
    answer: 'Yes! We provide user-friendly admin panels and training so you can easily manage your content and features.'
  },
  {
    question: 'What technologies do you specialize in?',
    answer: 'We work with React, Node.js, Python, Flutter, AWS, Azure, Shopify, WordPress, and more to deliver modern, scalable solutions.'
  },
  {
    question: 'How do you handle communication and progress updates?',
    answer: 'We use email, chat, and video calls to keep you updated with regular progress reports, demos, and feedback sessions.'
  },
  {
    question: 'Do you offer digital marketing or SEO services?',
    answer: 'Yes, we offer comprehensive digital marketing, SEO, and analytics services to help your business grow online.'
  },
  {
    question: 'Can you integrate third-party APIs or tools?',
    answer: 'Absolutely! We have extensive experience integrating payment gateways, CRMs, analytics, and other third-party services.'
  },
  {
    question: 'What makes your company different from others?',
    answer: 'We focus on results, transparency, and long-term partnerships. Our team is passionate about delivering measurable value and exceptional support.'
  }
];

const Faq: React.FC = () => {
  // Track open index for each column separately
  const [openLeft, setOpenLeft] = React.useState<number | null>(null);
  const [openRight, setOpenRight] = React.useState<number | null>(null);

  // Set page title
  useEffect(() => {
    document.title = 'FAQ - Frequently Asked Questions | App Developer Pro';
  }, []);

  const toggleLeft = (idx: number) => {
    setOpenLeft(openLeft === idx ? null : idx);
  };
  const toggleRight = (idx: number) => {
    setOpenRight(openRight === idx ? null : idx);
  };

  return (
    <div className="faq">
      <Helmet>
        <title>App Developer Pro - Web & Mobile Application Development Services | E-Commerce & Digital Solutions</title>
        <meta name="description" content="Get answers to frequently asked questions about web & mobile application development services, UI/UX design, e-commerce, and digital marketing. Start your project with App Developer Pro today!" />
        <meta name="keywords" content="FAQ, web development questions, app development, digital marketing, UI UX, e-commerce, project support, app developer pro" />
        <meta property="og:title" content="FAQ | App Developer Pro" />
        <meta property="og:description" content="Find answers to your web, app, and digital project questions. Learn about our process, support, and services." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://appdeveloperpro.com/faq" />
        <meta property="og:image" content="https://appdeveloperpro.com/images/faq-og.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FAQ | App Developer Pro" />
        <meta name="twitter:description" content="Answers to common questions about web development, mobile apps, and digital solutions. Get started with App Developer Pro!" />
        <meta name="twitter:image" content="https://appdeveloperpro.com/images/web-development-services.png" />
        <link rel="canonical" href="https://appdeveloperpro.com/faq" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What services do you offer?",
                "acceptedAnswer": { "@type": "Answer", "text": "We offer web development, mobile app development, UI/UX design, e-commerce solutions, digital marketing, cloud solutions, and DevOps." }
              },
              {
                "@type": "Question",
                "name": "How do I get a project quote?",
                "acceptedAnswer": { "@type": "Answer", "text": "Simply contact us through our website or email, and we will provide a free, detailed project quote after understanding your requirements." }
              },
              {
                "@type": "Question",
                "name": "What is your project delivery process?",
                "acceptedAnswer": { "@type": "Answer", "text": "Our process includes discovery, planning, design, development, testing, launch, and ongoing support. We keep you updated at every step." }
              },
              {
                "@type": "Question",
                "name": "Do you provide post-launch support?",
                "acceptedAnswer": { "@type": "Answer", "text": "Yes, we offer 24/7 support, maintenance, and updates to ensure your digital assets remain secure and up-to-date." }
              },
              {
                "@type": "Question",
                "name": "Are your solutions mobile-friendly and SEO-optimized?",
                "acceptedAnswer": { "@type": "Answer", "text": "Absolutely! All our solutions are fully responsive and built with SEO best practices for maximum visibility." }
              },
              {
                "@type": "Question",
                "name": "How do you ensure project quality?",
                "acceptedAnswer": { "@type": "Answer", "text": "We follow strict QA processes, code reviews, and user testing to deliver flawless, high-quality results." }
              },
              {
                "@type": "Question",
                "name": "Can you work with startups and enterprises?",
                "acceptedAnswer": { "@type": "Answer", "text": "Yes, we tailor our services for businesses of all sizes, from startups to large enterprises." }
              },
              {
                "@type": "Question",
                "name": "How long does a typical project take?",
                "acceptedAnswer": { "@type": "Answer", "text": "Project timelines vary, but most websites and apps are delivered within 4-12 weeks depending on complexity and requirements." }
              },
              {
                "@type": "Question",
                "name": "Will I be able to update my website/app myself?",
                "acceptedAnswer": { "@type": "Answer", "text": "Yes! We provide user-friendly admin panels and training so you can easily manage your content and features." }
              },
              {
                "@type": "Question",
                "name": "What technologies do you specialize in?",
                "acceptedAnswer": { "@type": "Answer", "text": "We work with React, Node.js, Python, Flutter, AWS, Azure, Shopify, WordPress, and more to deliver modern, scalable solutions." }
              },
              {
                "@type": "Question",
                "name": "How do you handle communication and progress updates?",
                "acceptedAnswer": { "@type": "Answer", "text": "We use email, chat, and video calls to keep you updated with regular progress reports, demos, and feedback sessions." }
              },
              {
                "@type": "Question",
                "name": "Do you offer digital marketing or SEO services?",
                "acceptedAnswer": { "@type": "Answer", "text": "Yes, we offer comprehensive digital marketing, SEO, and analytics services to help your business grow online." }
              },
              {
                "@type": "Question",
                "name": "Can you integrate third-party APIs or tools?",
                "acceptedAnswer": { "@type": "Answer", "text": "Absolutely! We have extensive experience integrating payment gateways, CRMs, analytics, and other third-party services." }
              },
              {
                "@type": "Question",
                "name": "What makes your company different from others?",
                "acceptedAnswer": { "@type": "Answer", "text": "We focus on results, transparency, and long-term partnerships. Our team is passionate about delivering measurable value and exceptional support." }
              }
            ]
          }
        `}</script>
      </Helmet>
      <section className="faq__hero">
        <div className="faq__hero-content">
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <FaQuestionCircle className="faq__hero-icon" /> Frequently Asked Questions
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
            Everything you need to know before starting your project. If you have more questions, <a href="/contact">contact us</a>!
          </motion.p>
        </div>
      </section>
      <section className="faq__list-section faq__list-section--two-col">
        <div className="faq__list-columns">
          <div className="faq__list-container faq__list-container--left">
            {faqsLeft.map((faq, idx) => (
              <motion.div
                className={`faq__item${openLeft === idx ? ' faq__item--open' : ''}`}
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
              >
                <button className="faq__question" onClick={() => toggleLeft(idx)}>
                  <span>{faq.question}</span>
                  <FaChevronDown className={`faq__chevron${openLeft === idx ? ' faq__chevron--open' : ''}`} />
                </button>
                <div className="faq__answer" style={{ maxHeight: openLeft === idx ? 200 : 0 }}>
                  <p>{faq.answer}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="faq__list-container faq__list-container--right">
            {faqsRight.map((faq, idx) => (
              <motion.div
                className={`faq__item${openRight === idx ? ' faq__item--open' : ''}`}
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
              >
                <button className="faq__question" onClick={() => toggleRight(idx)}>
                  <span>{faq.question}</span>
                  <FaChevronDown className={`faq__chevron${openRight === idx ? ' faq__chevron--open' : ''}`} />
                </button>
                <div className="faq__answer" style={{ maxHeight: openRight === idx ? 200 : 0 }}>
                  <p>{faq.answer}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Faq;

